import { useMemo, useState } from 'react';
import type { WorkspaceSettings, WorkspaceSummary } from '../../shared/workspaces';
import { api } from '../api';
import { useI18n } from '../i18n';
import { localizedError } from '../i18n/errors';
import { useUnsavedChanges } from '../unsaved-changes';

export function ArchivedWorkspacesPanel({ workspaces, onRestored }: {
  workspaces: WorkspaceSummary[];
  onRestored: (workspace: WorkspaceSettings) => Promise<void>;
}) {
  const { locale } = useI18n();
  const zh = locale === 'zh-CN';
  const archived = useMemo(() => workspaces.filter(item => item.status === 'archived'), [workspaces]);
  const [targetId, setTargetId] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  useUnsavedChanges(Boolean(targetId && password));

  if (!archived.length) return null;

  async function restore(workspace: WorkspaceSummary) {
    if (busy || !password || workspace.id !== targetId || !workspace.roles.includes('owner')) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const current = await api.workspaceSettings(workspace.id);
      const result = await api.restoreWorkspace(workspace.id, {
        currentPassword: password,
        revision: current.workspace.revision
      });
      setTargetId(''); setPassword('');
      setNotice(zh ? `「${workspace.name}」已恢复。` : `“${workspace.name}” was restored.`);
      await onRestored(result.workspace);
    } catch (err) {
      setError(localizedError(err, locale, {
        'zh-CN': '恢复 Workspace 失败。', en: 'Failed to restore the Workspace.'
      }));
    } finally { setBusy(false); }
  }

  return <section className="account-card archived-workspaces-card">
    <div><span className="eyebrow">Archive</span><h3>{zh ? '已归档的 Workspace' : 'Archived Workspaces'}</h3><p>{zh ? '归档空间不会参与日常工作台。原 Owner 验证当前密码后可以恢复。' : 'Archived Workspaces stay out of the active workbench. An existing Owner can restore one after verifying the current Account password.'}</p></div>
    {error && <p className="form-error">{error}</p>}
    {notice && <p className="form-success">{notice}</p>}
    <div className="session-list">
      {archived.map(workspace => {
        const owner = workspace.roles.includes('owner');
        const selected = targetId === workspace.id;
        return <article className="session-row" key={workspace.id}>
          <div><strong>{workspace.name}</strong><span>{owner ? (zh ? '你仍是 Owner' : 'You are still an Owner') : (zh ? '仅保留历史 Membership' : 'Historical membership only')}</span></div>
          <small>{workspace.kind} · {workspace.memberCount} {zh ? '位成员' : 'members'}</small>
          {owner ? <button className="text-button" type="button" disabled={busy} onClick={() => { setTargetId(selected ? '' : workspace.id); setPassword(''); setError(''); }}>{selected ? (zh ? '取消' : 'Cancel') : (zh ? '恢复' : 'Restore')}</button> : <span className="status-pill">{zh ? '不可恢复' : 'No restore access'}</span>}
          {selected && <div className="password-reset-panel archived-workspace-restore">
            <label>{zh ? '当前账号密码' : 'Current account password'}<input type="password" autoComplete="current-password" disabled={busy} value={password} onChange={event => setPassword(event.target.value)} /></label>
            <div className="row-actions"><button className="primary-button compact" type="button" disabled={busy || !password} onClick={() => { restore(workspace).catch(console.error); }}>{busy ? (zh ? '正在恢复…' : 'Restoring…') : (zh ? '确认恢复 Workspace' : 'Restore Workspace')}</button></div>
          </div>}
        </article>;
      })}
    </div>
  </section>;
}
